import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { eOrigemPadrao, escreverNaUrl, lerDaUrl, type FiltrosDaLista } from '../../../api/filtrosDeGmud'

/**
 * Quantos critérios estão estreitando a lista agora.
 *
 * Conta CRITÉRIO e não valor escolhido: marcar três estados é um filtro de estado, não três
 * filtros. É o número que vai no botão "Filtros", e com o painel recolhido ele é a única pista
 * de que a lista não está no recorte de sempre — sem isso, quem abre um link filtrado por outra
 * pessoa lê um recorte achando que é o todo.
 *
 * @param filtros critérios correntes
 * @returns quantos deles estão preenchidos
 */
export function quantidadeDeFiltros(filtros: FiltrosDaLista): number {
  return [
    filtros.status.length > 0,
    filtros.prioridade.length > 0,
    // A procedência só conta quando DIFERE do padrão. Contar `['V2']` faria a listagem limpa se
    // declarar filtrada e o "Limpar filtros" nascer aceso em toda visita, sem nada para limpar.
    // Qualquer outra procedência conta, o vazio inclusive: vazio quer dizer "as duas", que é um
    // recorte mais largo que o padrão e também precisa de caminho de volta.
    !eOrigemPadrao(filtros.origem),
    filtros.chamado.trim() !== '',
    filtros.q.trim() !== '',
  ].filter(Boolean).length
}

/**
 * Os filtros vivem na URL, não em `useState`.
 *
 * Três consequências que valem o trabalho: o fiscal cola no e-mail o link exato do que
 * está vendo; o botão "voltar" do navegador desfaz o filtro em vez de sair da tela; e
 * recarregar a página não zera a triagem. No sistema anterior o filtro era estado local,
 * e a resposta para "me manda essa lista" era um print.
 *
 * @returns os filtros correntes e as funções que os alteram
 */
export function useFiltrosNaUrl() {
  const [parametros, definirParametros] = useSearchParams()

  const filtros = useMemo(() => lerDaUrl(parametros), [parametros])

  const alterar = useCallback(
    (mudanca: Partial<FiltrosDaLista>) => {
      // Mudar qualquer critério volta para a primeira página: senão o usuário filtra
      // estando na página 4 e recebe uma tela vazia que parece "nenhum resultado".
      const proximo = { ...filtros, ...mudanca }
      if (mudanca.pagina === undefined) {
        proximo.pagina = 1
      }
      // Empilha no histórico em vez de sobrescrever a entrada atual. Com `replace`, a
      // segunda promessa acima não se cumpre: "voltar" pularia toda a triagem de uma vez
      // e sairia da lista, que é exatamente o que se queria evitar. Aqui cada critério
      // aplicado é um passo, e um passo se desfaz.
      definirParametros(escreverNaUrl(proximo))
    },
    [filtros, definirParametros],
  )

  const limpar = useCallback(() => {
    definirParametros(new URLSearchParams())
  }, [definirParametros])

  // `origem` entra na conta quando difere do padrão, e o caso que isso protege continua sendo o
  // mesmo: o item de menu "Histórico do sistema anterior" (que é `/gmuds?origem=MIGRADO_V1`) tem
  // de chegar numa tela que se sabe filtrada. Enquanto a procedência ficava fora da conta, o
  // botão "Limpar filtros" nascia desabilitado ali e, quando o recorte não achava nada, a tela
  // dizia "ainda não há solicitações" — com 1.027 delas no banco.
  const quantidade = quantidadeDeFiltros(filtros)

  return { filtros, alterar, limpar, temFiltro: quantidade > 0, quantidade }
}
